// The frame every auth screen sits in. It is the shell's own grammar, not a
// centred SaaS card: a 44 px floor strip carrying the mark and the screen's
// state, a panel-step body whose single column is anchored on the gutter the
// product rail defines, and a 24 px floor strip along the bottom. A person who
// has seen the Terminal recognises the door as the same product; a person who
// has not sees a framed page rather than a 400 px column adrift on 900 px of
// empty ground.
//
// The mark is not a link here. Off the shell it would point at ?view=live,
// and one click on the logo of an invitation, verification or reset screen
// stripped the one-time token out of the URL with no way back except the
// email (redo-2 P1).
import type { ReactNode } from 'react';
import { AlephMark } from '../../shell/TopBar';
import { WorkspaceMap } from './WorkspaceMap';
import './auth.css';

export interface AuthFrameProps {
  readonly title: string;
  readonly lede?: ReactNode;
  readonly children: ReactNode;
  readonly foot?: ReactNode;
  // The state pill for the strip at the top: what this screen is waiting on.
  readonly state?: ReactNode;
  // The second column: quiet, real context. Defaults to the registry this
  // door opens onto, which is what a person holding an invitation actually
  // wants to know. Pass null for a screen that should stand alone.
  readonly aside?: ReactNode;
  // The middle cell of the bottom strip: one line about the access check.
  readonly statusNote?: ReactNode;
  // The bottom strip's first cell. Defaults to the screen title.
  readonly where?: string;
}

export function AuthFrame({ title, lede, children, foot, state, aside = <WorkspaceMap />, statusNote, where }: AuthFrameProps) {
  return (
    <div className="door">
      <header className="door-bar">
        <AlephMark linked={false} />
        <span className="door-kind">Terminal</span>
        {state ? <span className="door-bar-end">{state}</span> : null}
      </header>
      {/* A landmark, not a div. The door is the only screen in the product
          that is not the shell, and it shipped with no <main> at all: axe
          reported landmark-one-main plus three region violations at 1280 and
          390, because the h1 and everything under it sat outside every
          landmark. The shell has always had main[data-region="work"]; this is
          the door's own. */}
      <main className="door-body" aria-label={where ?? title}>
        <div className="door-cols">
          <div className="door-col">
            <h1 className="door-t">{title}</h1>
            {lede ? <p className="door-lede">{lede}</p> : null}
            <div className="door-body-in">{children}</div>
            {foot ? <div className="door-foot">{foot}</div> : null}
          </div>
          {aside ? <aside className="door-aside" aria-label="What this workspace contains">{aside}</aside> : null}
        </div>
      </main>
      <footer className="door-status">
        <span className="door-status-where"><b>{where ?? title}</b></span>
        {/* A standing fact, and the same one the lede and the foot already
            carry. Under 768 it gives way so the access-check note can print in
            full: at 390 the note needed 216 px and had 206, so the one live
            fact in the strip was the one truncated. */}
        <span className="door-status-sep door-status-standing" />
        <span className="door-status-standing">Invitation only</span>
        {statusNote ? <><span className="door-status-sep" /><span className="door-status-note">{statusNote}</span></> : null}
      </footer>
    </div>
  );
}
